import { Dispatch, SetStateAction, useEffect, useRef } from 'react';

import InteractiveImageContent from './InteractiveImageContent';
import PDFContent from './PDFContent';
import QuizContent from './QuizContent';
import SelectSurveyContent from './SelectSurveyContent';
import TextContent from './TextContent';
import TextSurveyContent from './TextSurveyContent';
import VideoContent from './VideoContent';
import { ContentData, ContentType } from '../../../types/contentTypes';
import { useInViewport } from '../../../utils/hooks';

import './BaseSection.scss';

export type CompletionResult = {
  sectionComplete: boolean;
  contentComplete: boolean;
};

interface BaseContentProps {
  content: ContentData;
  /**
   * Whether the current user is enrolled in the skill containing this content. This will decide whether the user makes
   * progress or not when completing the section.
   */
  enrolled?: boolean;
  onComplete: () => void;
  className?: string;
  setCurrentContent?: Dispatch<SetStateAction<string>>;
}

export default function BaseContent({ content, onComplete, className = '', setCurrentContent }: BaseContentProps) {
  const onCompleteCalled = useRef<boolean>(false);
  const { elementRef, isInViewPort } = useInViewport();

  function handleComplete() {
    if (!onCompleteCalled.current) {
      onComplete();
      onCompleteCalled.current = true;
    }
  }

  useEffect(() => {
    if (!isInViewPort) return;
    if (setCurrentContent) {
      setCurrentContent(content.id);
    }
  }, [isInViewPort, setCurrentContent, content.id]);

  return (
    <div className={`base-section ${className}`} ref={elementRef}>
      {content.type === ContentType.Text && <TextContent content={content} onComplete={handleComplete} />}
      {content.type === ContentType.Quiz && <QuizContent content={content} onComplete={handleComplete} />}
      {content.type === ContentType.Pdf && <PDFContent content={content} onComplete={handleComplete} />}
      {content.type === ContentType.Video && <VideoContent content={content} onComplete={handleComplete} />}
      {content.type === ContentType.InteractiveImage && (
        <InteractiveImageContent content={content} onComplete={handleComplete} />
      )}
      {content.type === ContentType.TextSurvey && <TextSurveyContent content={content} onComplete={handleComplete} />}
      {content.type === ContentType.SelectSurvey && (
        <SelectSurveyContent content={content} onComplete={handleComplete} />
      )}
    </div>
  );
}
